import { z } from 'zod';
import { montarCaminho, requisitar } from './client.js';
import type { Pagina, ParametrosDePagina } from './paginacao.js';
import { paginaSchema, parametrosDePagina } from './paginacao.js';
import { analisar } from './validacao.js';

/**
 * Dispositivos do armazém (UC12, RF-003/RF-004).
 *
 * O pareamento devolve o código UMA única vez — o banco guarda só o hash. O
 * painel o mostra na tela (QR gerado localmente) e nunca o guarda.
 */

const CAMINHO_COLECAO = montarCaminho('/dispositivos');

/**
 * Autor gravado quando o freio de força bruta do login do coletor derrubou o
 * aparelho sozinho. Ninguém decidiu nada: a tela precisa dizer isso, porque
 * desbloquear aí é uma decisão nova, não desfazer a de alguém.
 */
export const AUTOR_DO_FREIO_AUTOMATICO = 'sistema:freio_de_autenticacao';

export const statusDispositivoSchema = z.enum(['AguardandoPareamento', 'Ativo', 'Bloqueado']);

export type StatusDispositivo = z.infer<typeof statusDispositivoSchema>;

export const operadorNoDispositivoSchema = z.object({
  id: z.string(),
  nome: z.string(),
  matricula: z.string(),
});

export const dispositivoSchema = z.object({
  id: z.string(),
  identificador: z.string(),
  apelido: z.string().nullable(),
  modelo: z.string().nullable(),
  versaoApp: z.string().nullable(),
  status: statusDispositivoSchema,
  ultimaSync: z.string().nullable(),
  /** Auto-declarada pelo aparelho no último batimento (UC12 2b). */
  operacoesPendentes: z.number().int().nonnegative(),
  pareadoEm: z.string().nullable(),
  /** Há código de pareamento válido agora. */
  pareamentoPendente: z.boolean(),
  pareamentoExpiraEm: z.string().nullable(),
  bloqueadoEm: z.string().nullable(),
  bloqueadoPor: z.string().nullable(),
  criadoEm: z.string(),
  operador: operadorNoDispositivoSchema.nullable(),
});

export type Dispositivo = z.infer<typeof dispositivoSchema>;

const paginaDeDispositivosSchema = paginaSchema(dispositivoSchema);

export const pareamentoGeradoSchema = z.object({
  /** Devolvido UMA única vez. Não guardar, não registrar em log. */
  codigo: z.string(),
  expiraEm: z.string(),
  /** JSON compacto que o painel renderiza como QR, localmente (RNF-026). */
  conteudoQr: z.string(),
});

export type PareamentoGerado = z.infer<typeof pareamentoGeradoSchema>;

export type FiltroDeDispositivos = ParametrosDePagina & {
  readonly busca?: string;
  readonly status?: StatusDispositivo;
};

export type CadastroDeDispositivo = {
  readonly identificador: string;
  readonly apelido?: string;
};

export type AlteracaoDeDispositivo = {
  readonly identificador?: string;
  /** `null` limpa o apelido. */
  readonly apelido?: string | null;
};

export async function listarDispositivos(
  filtro: FiltroDeDispositivos = {},
): Promise<Pagina<Dispositivo>> {
  const corpo = await requisitar(CAMINHO_COLECAO, {
    consulta: { ...parametrosDePagina(filtro), busca: filtro.busca, status: filtro.status },
  });

  return analisar(paginaDeDispositivosSchema, corpo, CAMINHO_COLECAO);
}

export async function obterDispositivo(id: string): Promise<Dispositivo> {
  const caminho = montarCaminho('/dispositivos/{id}', { id });
  return analisar(dispositivoSchema, await requisitar(caminho), caminho);
}

export async function criarDispositivo(cadastro: CadastroDeDispositivo): Promise<Dispositivo> {
  const corpo = await requisitar(CAMINHO_COLECAO, { metodo: 'POST', corpo: cadastro });
  return analisar(dispositivoSchema, corpo, CAMINHO_COLECAO);
}

export async function atualizarDispositivo(
  id: string,
  mudancas: AlteracaoDeDispositivo,
): Promise<Dispositivo> {
  const caminho = montarCaminho('/dispositivos/{id}', { id });
  const corpo = await requisitar(caminho, { metodo: 'PATCH', corpo: mudancas });
  return analisar(dispositivoSchema, corpo, caminho);
}

/** Gera o código de pareamento e o conteúdo do QR. Vale 15 minutos. */
export async function gerarPareamento(id: string): Promise<PareamentoGerado> {
  const caminho = montarCaminho('/dispositivos/{id}/pareamento', { id });
  const corpo = await requisitar(caminho, { metodo: 'POST' });
  return analisar(pareamentoGeradoSchema, corpo, caminho);
}

/**
 * Bloqueio integral (RF-004): o aparelho para de falar com o middleware em
 * qualquer rota. Operações ainda não sincronizadas podem se perder — a tela
 * confirma antes.
 */
export async function bloquearDispositivo(id: string): Promise<Dispositivo> {
  const caminho = montarCaminho('/dispositivos/{id}/bloquear', { id });
  const corpo = await requisitar(caminho, { metodo: 'POST' });
  return analisar(dispositivoSchema, corpo, caminho);
}

export async function desbloquearDispositivo(id: string): Promise<Dispositivo> {
  const caminho = montarCaminho('/dispositivos/{id}/desbloquear', { id });
  const corpo = await requisitar(caminho, { metodo: 'POST' });
  return analisar(dispositivoSchema, corpo, caminho);
}

/** Distingue o freio de força bruta de um bloqueio que alguém decidiu. */
export function bloqueioFoiAutomatico(dispositivo: Dispositivo): boolean {
  return (
    dispositivo.status === 'Bloqueado' && dispositivo.bloqueadoPor === AUTOR_DO_FREIO_AUTOMATICO
  );
}
